import { type BlockConfig, iconField } from './_types';

/**
 * Payload `Block` config sidecar for the `pillarGrid` block.
 *
 * Field tree mirrors the props/data the React component (`./pillar-grid.tsx`)
 * needs to render the production `FeaturePillars` pattern (nested 3-pillar x
 * N-feature grid). See the cutover note in `./_types.ts` for the 5-line
 * `cp + sed` that moves this file into `apps/marketing/payload/blocks/` at
 * Next.js cutover.
 *
 * Slug + interfaceName naming reconciliation:
 *   File name:               `pillar-grid.tsx` / `pillar-grid.payload.ts`
 *   React component export:  `PillarGrid`
 *   Payload `slug`:          `pillarGrid`
 *   Payload `interfaceName`: `PillarGridBlock`
 */

export const PillarGridBlock: BlockConfig = {
  slug: 'pillarGrid',
  interfaceName: 'PillarGridBlock',
  labels: {
    singular: 'Pillar Grid',
    plural: 'Pillar Grid Blocks',
  },
  fields: [
    {
      name: 'eyebrow',
      type: 'text',
      label: 'Eyebrow / Badge',
      admin: {
        description:
          'Small pill label above the section headline. Example: "What You Can Do".',
      },
    },
    {
      name: 'headline',
      type: 'textarea',
      required: true,
      label: 'Headline',
      admin: { rows: 2 },
    },
    {
      name: 'subheadline',
      type: 'textarea',
      label: 'Subheadline',
      admin: { rows: 2 },
    },
    {
      name: 'bgVariant',
      type: 'select',
      label: 'Background variant',
      defaultValue: 'muted',
      options: [
        { label: 'Default (background)', value: 'default' },
        { label: 'Muted (subtle gray)', value: 'muted' },
      ],
    },
    {
      name: 'pillars',
      type: 'array',
      required: true,
      minRows: 1,
      maxRows: 5,
      labels: { singular: 'Pillar', plural: 'Pillars' },
      label: 'Pillars',
      fields: [
        {
          name: 'badge',
          type: 'text',
          required: true,
          label: 'Badge (eyebrow text)',
          admin: {
            description:
              'Short pillar label. Examples: "Share", "Earn", "Control".',
          },
        },
        {
          name: 'htmlId',
          type: 'text',
          label: 'Anchor id (optional)',
          admin: {
            description:
              'Optional HTML id rendered on this pillar so external links can deep-link to it (e.g. "/how-it-works#earn"). Lowercase, hyphenated. Leave blank if no anchor is needed.',
          },
        },
        {
          name: 'title',
          type: 'text',
          required: true,
          label: 'Pillar title',
        },
        {
          name: 'subtitle',
          type: 'textarea',
          label: 'Pillar subtitle',
          admin: { rows: 2 },
        },
        {
          name: 'accentColor',
          type: 'select',
          label: 'Accent color',
          defaultValue: 'blue',
          options: [
            { label: 'Blue', value: 'blue' },
            { label: 'Purple', value: 'purple' },
            { label: 'Amber', value: 'amber' },
            { label: 'Green', value: 'green' },
            { label: 'Red', value: 'red' },
          ],
        },
        {
          name: 'features',
          type: 'array',
          required: true,
          minRows: 1,
          maxRows: 8,
          labels: { singular: 'Feature', plural: 'Features' },
          label: 'Sub-features',
          fields: [
            {
              ...iconField({
                name: 'icon',
                label: 'Icon (lucide-react name)',
                required: true,
              }),
              admin: {
                description:
                  'PascalCase lucide-react icon name. Supported icons include MessageSquare, Target, Users, Share2, Trophy, Calendar, Coins, TrendingUp, Eye, Shield, AlertTriangle, Scale, Lock, Zap, Trash2, FileCheck, Sparkles, Award, BadgeCheck. Unknown names fall back to Sparkles.',
              },
            },
            {
              name: 'title',
              type: 'text',
              required: true,
              label: 'Title',
            },
            {
              name: 'description',
              type: 'textarea',
              required: true,
              label: 'Description',
              admin: { rows: 2 },
            },
          ],
        },
      ],
    },
  ],
};

export default PillarGridBlock;
